<template>
    <v-row v-if="customer" no-gutters class="bg-white pa-6 rounded-md">
        <v-col cols="12" class="d-flex justify-end mt-n2 ml-2">
            <v-btn icon color="black" absolute @click="$emit('closeInfo')">
                <v-icon>mdi-close</v-icon>
            </v-btn>
        </v-col>
        <v-col cols="12">
            <!-- Header: Customer Name -->
            <v-row no-gutters justify="center">
                <v-col cols="auto" class="text-center">
                    <h2 class="text-2xl font-bold">{{ customer.name }}</h2>
                </v-col>
            </v-row>
            
            <!-- Personal Info Section -->
            <v-row class="mt-4">
                <v-col cols="12">
                    <v-card class="pa-3 rounded-xl" flat color="surface" elevation="0">
                        <v-row no-gutters>
                            <v-col cols="12" md="4">
                                <v-row dense>
                                    <v-col v-if="customer.birthday" cols="12" class="text-body-2">
                                        <v-icon small left>mdi-cake-variant</v-icon>
                                        {{ getAge(customer.birthday) }} anos ({{ formatDate(customer.birthday) }})
                                    </v-col>
                                    <v-col v-if="customer.cpf" cols="12" class="text-body-2">
                                        <v-icon small left>mdi-card-account-details</v-icon>
                                        {{ customer.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") }}
                                    </v-col>
                                </v-row>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-row dense>
                                    <v-col v-if="customer.phone" cols="12" class="text-body-2">
                                        <v-icon small left>mdi-whatsapp</v-icon>
                                        <a :href="'https://wa.me/+' + customer.phone" target="_blank" class="text-decoration-none black--text">
                                            {{ customer.phone?.replace(/(\d{2})(\d{2})(\d{5}|\d{4})(\d{4})/, "+$1 ($2) $3-$4") }}
                                        </a>
                                    </v-col>
                                    <v-col v-if="customer.email" cols="12" class="text-body-2 text-no-wrap">
                                        <v-icon small left>mdi-email</v-icon>
                                        <a :href="'mailto:' + customer.email" target="_blank" class="text-decoration-none black--text">{{ customer.email }}</a>
                                    </v-col>
                                </v-row>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-row dense>
                                    <v-col cols="12" class="text-body-2">
                                        <v-icon small left>mdi-cash</v-icon>
                                        Saldo: <b>{{ (customer.customer_doctor.balance / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</b>
                                        <v-tooltip v-if="customer.customer_doctor.balance <= 0" bottom :color="customer.customer_doctor.balance === 0 ? 'event-payment-pending' : 'error'">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon right v-bind="attrs" v-on="on" :color="customer.customer_doctor.balance === 0 ? 'event-payment-pending' : 'error'" class="mt-n2">mdi-information</v-icon>
                                            </template>
                                            <span>Saldo {{ customer.customer_doctor.balance === 0 ? 'zerado' : 'negativo' }} </span>
                                        </v-tooltip>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Session Info Card (only when session_info exists) -->
            <v-row v-if="session_info" class="mt-4">
                <v-col cols="12">
                    <v-card class="pa-4 rounded-xl white--text position-relative" flat color="primary" elevation="0">
                        <div class="position-absolute d-flex justify-end mt-n7 mr-n2">
                            <v-chip
                                small
                                :color="getSessionStatusColor(session_info)"
                                class="white--text font-weight-bold"
                            >
                                {{ getSessionStatusText(session_info) }}
                            </v-chip>
                        </div>
                        <v-row align="center" justify="space-between">
                            <v-col cols="12" sm="6" md="4">
                                <div class="d-flex align-center">
                                    <v-icon color="white" left>mdi-clipboard-text-outline</v-icon>
                                    <div>
                                        <div class="text-caption">Serviço</div>
                                        <div class="text-subtitle-1 font-weight-bold">{{ session_info.service?.name }}</div>
                                    </div>
                                </div>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <div class="d-flex align-center">
                                    <v-icon color="white" left>mdi-calendar-clock</v-icon>
                                    <div>
                                        <div class="text-caption">Data e Hora</div>
                                        <div class="text-subtitle-1 font-weight-bold">{{ formatDate(session_info.session_date, true) }}</div>
                                    </div>
                                </div>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <div class="d-flex align-center">
                                    <v-icon color="white" left>mdi-cash</v-icon>
                                    <div>
                                        <div class="text-caption">Valor</div>
                                        <div v-if="session_info && typeof session_info.session_value === 'number'" class="text-subtitle-1 font-weight-bold">{{ (session_info.session_value / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</div>
                                        <div v-else class="text-subtitle-1 font-weight-bold">--</div>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                        
                        <!-- Google Calendar Integration Info -->
                        <v-row v-if="session_info && session_info.google_calendar_event" class="mt-2" no-gutters>
                            <v-col cols="12">
                                <v-divider class="my-2 white" style="opacity: 0.3"></v-divider>
                            </v-col>
                            <v-col cols="12" class="d-flex align-center justify-space-between">
                                <div class="d-flex align-center">
                                    <v-icon color="white" left small>mdi-google</v-icon>
                                    <span class="text-caption white--text mr-2">Google Calendar</span>
                                    
                                    <v-tooltip bottom v-if="checkTimeDiscrepancy(session_info)">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon 
                                                small 
                                                color="yellow accent-2"
                                                v-bind="attrs"
                                                v-on="on"
                                                class="mr-2"
                                            >
                                                mdi-alert
                                            </v-icon>
                                        </template>
                                        <span>Horário diferente do sistema</span>
                                    </v-tooltip>
                                    
                                    <v-btn 
                                        v-if="checkTimeDiscrepancy(session_info)"
                                        x-small 
                                        color="white" 
                                        light 
                                        class="px-2"
                                        @click="syncEventToGoogle(session_info)"
                                        :loading="loadingSync"
                                    >
                                        <v-icon left x-small>mdi-sync</v-icon>
                                        Sincronizar
                                    </v-btn>
                                </div>
                                
                                <v-btn 
                                    v-if="session_info.google_calendar_event.hangout_link"
                                    small 
                                    color="white" 
                                    outlined 
                                    :href="session_info.google_calendar_event.hangout_link" 
                                    target="_blank"
                                >
                                    <v-icon left small>mdi-video</v-icon>
                                    Entrar no Meet
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Reschedule Requests Card -->
            <v-row v-if="customerRescheduleRequests.length" class="mt-4">
                <v-col cols="12">
                    <v-card class="pa-4 rounded-xl white--text position-relative" flat color="secondary-yellow darken-1" elevation="0">
                        <v-row align="center" justify="space-between">
                            <v-col cols="auto">
                                <div class="d-flex align-center">
                                    <v-icon color="white" left>mdi-calendar-refresh</v-icon>
                                    <div>
                                        <div class="text-caption">Solicitações de Reagendamento</div>
                                        <div class="text-subtitle-1 font-weight-bold">
                                            {{ customerRescheduleRequests.length }} pendente{{ customerRescheduleRequests.length > 1 ? 's' : '' }}
                                        </div>
                                    </div>
                                </div>
                            </v-col>
                            <v-col cols="auto">
                                <v-btn 
                                    small 
                                    color="white" 
                                    @click="showRescheduleDialog = true"
                                >
                                    <v-icon left small>mdi-eye</v-icon>
                                    Ver Solicitações
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Action Buttons -->
            <v-row class="mt-2" justify="center">
                <v-col v-if="session_info && !session_info?.finished" cols="auto">
                    <v-tooltip bottom color="primary lighten-2">
                        <template #activator="{ attrs, on }">
                            <v-btn fab small color="primary lighten-2" class="white--text" @click="openConfirmSession" v-bind="attrs" v-on="on" id="tour-schedule-confirm-btn">
                                <v-icon>mdi-head-check-outline</v-icon>
                            </v-btn>
                        </template>
                        Confirmar atendimento
                    </v-tooltip>
                </v-col>
                <v-col v-if="session_info" cols="auto">
                    <v-tooltip bottom color="primary">
                        <template #activator="{ attrs, on }">
                            <v-btn fab small color="primary" class="white--text" dark @click="openEditSession(session_info)" v-bind="attrs" v-on="on">
                                <v-icon>mdi-calendar-edit</v-icon>
                            </v-btn>
                        </template>
                        Editar atendimento
                    </v-tooltip>
                </v-col>
                <v-col v-if="session_info" cols="auto">
                    <v-tooltip bottom color="primary-blue">
                        <template #activator="{ attrs, on }">
                            <v-btn fab small color="primary-blue" class="white--text" dark @click="openMedicalRecord(session_info)" v-bind="attrs" v-on="on" :disabled="isStarter" id="tour-schedule-medical-record-btn">
                                <v-icon>mdi-clipboard-text-outline</v-icon>
                            </v-btn>
                        </template>
                        {{ isStarter ? 'Prontuário (Premium)' : 'Prontuário do atendimento' }}
                    </v-tooltip>
                </v-col>
                <v-col cols="auto">
                    <v-tooltip bottom color="primary-pink">
                        <template #activator="{ attrs, on }">
                            <v-btn fab small color="primary-pink" class="white--text" dark @click="openExportModal" v-bind="attrs" v-on="on" :disabled="isStarter" id="tour-schedule-export-btn">
                                <v-icon>mdi-file-pdf-box</v-icon>
                            </v-btn>
                        </template>
                        {{ isStarter ? 'Exportar Prontuários (Premium)' : 'Exportar Prontuários' }}
                    </v-tooltip>
                </v-col>
                <v-col cols="auto">
                    <v-tooltip bottom color="green">
                        <template #activator="{ attrs, on }">
                            <v-btn fab small color="green" class="white--text" @click="openAddExtract" v-bind="attrs" v-on="on">
                                <v-icon>mdi-cash-plus</v-icon>
                            </v-btn>
                        </template>
                        Adicionar pagamento
                    </v-tooltip>
                </v-col>
                <v-col cols="auto">
                    <v-tooltip bottom color="secondary">
                        <template #activator="{ attrs, on }">
                            <v-btn fab small color="secondary" class="white--text" @click="openAddSession" v-bind="attrs" v-on="on">
                                <v-icon>mdi-clock-plus-outline</v-icon>
                            </v-btn>
                        </template>
                        Agendar atendimento extra
                    </v-tooltip>
                </v-col>
                <v-col v-if="session_info" cols="auto">
                    <v-tooltip bottom color="error">
                        <template #activator="{ attrs, on }">
                            <v-btn fab small color="error" class="white--text" @click="openDeleteSession(session_info)" v-bind="attrs" v-on="on">
                                <v-icon>mdi-clock-remove-outline</v-icon>
                            </v-btn>
                        </template>
                        {{ session_info?.finished ? 'Excluir atendimento' : 'Remover agendamento' }}
                    </v-tooltip>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-divider></v-divider>
                </v-col>
            </v-row>

            <!-- Tabs Section -->
            <v-row>
                <v-col>
                    <v-tabs v-model="service_tab" background-color="transparent" color="primary">
                        <v-tab key="geral">Geral</v-tab>
                        <v-tab v-for="contract in customer.active_contracts" :key="contract.service_id">
                            {{ contract.service.name }}
                        </v-tab>
                    </v-tabs>
                </v-col>
                <v-col cols="auto">
                    <v-tooltip top color="primary-blue">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn color="primary" outlined small elevation="0" @click="showSessions(customer)" v-bind="attrs" v-on="on">
                                <v-icon small>mdi-list-box-outline</v-icon>
                            </v-btn>
                        </template>
                        Ver todas
                    </v-tooltip>
                </v-col>
            </v-row>
            
            <!-- Tab Content: Charts Carousel -->
            <v-row class="mt-2">
                <v-col cols="12">
                    <v-card class="pa-3 rounded-xl" flat color="white" elevation="0">
                        <v-row align="center" justify="space-between" class="flex-nowrap">
                            <v-col cols="auto">
                                <v-btn icon @click="chart_index = chart_index > 0 ? chart_index - 1 : 1" :disabled="chart_index === 0">
                                    <v-icon>mdi-chevron-left</v-icon>
                                </v-btn>
                            </v-col>
                            <v-col>
                                <v-window v-model="chart_index" class="elevation-0">
                                    <!-- Desempenho Chart -->
                                    <v-window-item :value="0">
                                        <v-row>
                                            <v-col cols="12">
                                                <span class="text-h6">Desempenho</span>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12">
                                                <apexchart 
                                                    type="donut"
                                                    :options="chartOptions"
                                                    :series="donutSeries"
                                                    width="100%"
                                                    height="250"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-window-item>
                                    
                                    <!-- Histórico Chart -->
                                    <v-window-item :value="1">
                                        <v-row>
                                            <v-col>
                                                <span class="text-h6">Histórico</span>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12">
                                                <apexchart type="bar" height="250" :options="chartOptionsBar" :series="seriesSessionsChart" />
                                            </v-col>
                                        </v-row>
                                    </v-window-item>
                                </v-window>
                            </v-col>
                            <v-col cols="auto">
                                <v-btn icon @click="chart_index = chart_index < 1 ? chart_index + 1 : 0" :disabled="chart_index === 1">
                                    <v-icon>mdi-chevron-right</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-divider></v-divider>
                </v-col>
            </v-row>

            <!-- Payments Section -->
            <v-row>
                <v-col>
                    <v-row>
                        <v-col>
                            <h2>Pagamentos</h2>
                        </v-col>
                        <v-col cols="auto">
                            <v-tooltip top color="primary">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn color="primary" small outlined elevation="0" @click="showCustomerPayments(customer)" v-bind="attrs" v-on="on">
                                        <v-icon small>mdi-list-box-outline</v-icon>
                                    </v-btn>
                                </template>
                                Ver todos
                            </v-tooltip>
                        </v-col>
                    </v-row>
                    <v-card class="pa-4 mt-4 rounded-xl" flat color="white" elevation="0">
                        <v-row v-if="lastPayment">
                            <v-col cols="12" md="6">
                                <div class="text-subtitle-2 text--secondary">Último Pagamento</div>
                                <div class="text-h6">{{ formatDate(lastPayment.extract_date) }}</div>
                                <div class="text-h5 primary--text">{{ (lastPayment.ammount_paid / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</div>
                            </v-col>
                            <v-col cols="12" md="6">
                                <div class="text-subtitle-2 text--secondary">Próximo Pagamento</div>
                                <div class="text-h6">{{ nextPaymentDate }}</div>
                            </v-col>
                        </v-row>
                        <v-row v-else>
                            <v-col cols="12" class="text-center text--secondary">
                                Nenhum pagamento registrado
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>
        </v-col>
        <dialog-add-extract :customer-data="customer_data" :session-info="session_info" v-model="show_add_extract" @close="closeAddExtract" @created="getCustomerInfo(true)" />
        <dialog-confirm-session :customer-data="customer_data" :session-data="session_data" v-model="show_confirm_session" @close="closeConfirmSession" @created="getCustomerInfo(true)"/>
        <dialog-add-session :customer-data="customer_data" v-model="show_add_session" @close="closeAddSession" @created="getCustomerInfo(true)"/>
        <dialog-edit-extract :customer-data="customer_data" :extract-data="extract_data" v-model="show_edit_extract" @close="closeEditExtract" @edited="getCustomerInfo(true)" />
        <dialog-edit-session :customer-data="customer_data" :session-data="session_data" v-model="show_edit_session" @close="closeEditSession" @edited="getCustomerInfo(true)" />
        <dialog-delete-extract :customer-data="customer_data" :extract-data="extract_data" v-model="show_delete_extract" @close="closeDeleteExtract" @deleted="getCustomerInfo(true)" />
        <dialog-delete-session :customer-data="customer_data" :session-data="session_data" v-model="show_delete_session" @close="closeDeleteSession" @deleted="getCustomerInfo(true, true)" />
        <dialog-list-sessions :customer-data="customer_data" v-model="show_list_sessions" @close="closeListSessions" @updateData="getCustomerInfo(true)"/>
        <dialog-list-payments :customer-data="customer_data" v-model="show_list_customer_payments" @close="closeCustomerPayments" @updateData="getCustomerInfo(true)" />
        <dialog-reschedule-requests :customer-id="customerId" v-model="showRescheduleDialog" @requestUpdated="getCustomerInfo(true)" />
        <dialog-export-medical-records v-model="showDialogExport" ref="exportModal" />
        <dialog-medical-record v-if="session_data" v-model="show_medical_record" :session-data="session_data" @saved="getCustomerInfo(true)" />
    </v-row>
</template>

<script>
import DialogAddSession from '../../sessions/components/DialogAddSession.vue';
import DialogConfirmSession from '../../sessions/components/DialogConfirmSession.vue';
import DialogAddExtract from '../../extract/components/DialogAddExtract.vue';
import DialogEditExtract from '../../extract/components/DialogEditExtract.vue';
import DialogEditSession from '../../sessions/components/DialogEditSession.vue';
import DialogDeleteExtract from '../../extract/components/DialogDeleteExtract.vue';
import DialogDeleteSession from '../../sessions/components/DialogDeleteSession.vue';
import DialogListSessions from '../../sessions/components/DialogListSessions.vue';
import DialogListPayments from '../../extract/components/DialogListPayments.vue';
import DialogRescheduleRequests from './DialogRescheduleRequests.vue';
import DialogExportMedicalRecords from '../../sessions/components/DialogExportMedicalRecords.vue';
import DialogMedicalRecord from '../../sessions/components/DialogMedicalRecord.vue';
import dayjs from 'dayjs';
import { mapActions, mapState } from 'vuex';

export default {
    components: {
        DialogAddSession,
        DialogConfirmSession,
        DialogAddExtract,
        DialogEditExtract,
        DialogEditSession,
        DialogDeleteExtract,
        DialogDeleteSession,
        DialogListSessions,
        DialogListPayments,
        DialogRescheduleRequests,
        DialogExportMedicalRecords,
        DialogMedicalRecord
    },
    props: {
        customerId: {
            type: Number,
            required: false,
            default: null
        },
        sessionInfo: {
            type: Object,
            required: false,
            default: null
        }
    },
    data() {
        return {
            customer_data: null,
            extract_data: null,
            session_data: null,
            show_add_session: false,
            show_confirm_session: false,
            show_add_extract: false,
            show_edit_extract: false,
            show_edit_session: false,
            show_delete_extract: false,
            show_delete_session: false,
            show_list_sessions: false,
            show_list_customer_payments: false,
            headers_extract: [
                { text: "Data", value: "extract_date", sortable: false },
                { text: "Valor", value: "ammount_paid", sortable: false },
                { text: '', value: 'actions', sortable: false },
            ],
            headers_sessions: [
                { text: "Data", value: "session_date", sortable: false },
                { text: "Compareceu?", value: "attended", sortable: false },
                { text: "Prontuário", value: "session_record", sortable: false },
                { text: '', value: 'actions', sortable: false },
            ],
            customer: null,
            chartOptions: {

                labels: ['Compareceu', 'Faltou', 'Remarcou'],
                colors: [this.$vuetify.theme.themes.light['event-attended'], this.$vuetify.theme.themes.light['event-missed'], this.$vuetify.theme.themes.light['event-rescheduled']],
                dataLabels: {
                    enable: true,
                    formatter: function (val) {
                        return Math.round(val) + "%";
                    }
                },
                legend: {
                    show: false
                },
                plotOptions: {
                    pie: {
                        expandOnClick: false,
                        donut: {
                            size: '50%',
                            labels: {
                                show: true,
                                name: {
                                    show: true,
                                    color: '#000'
                                },
                                value: {
                                    show: true,
                                    color: '#000'
                                },
                                total: {
                                    show: true,
                                    showAlways: true,
                                    color: '#000'
                                }
                            }
                        }
                    }
                },
                stroke: {
                    show: false
                }
            },
            session_info: null,
            chartOptionsBar: {
                colors: [this.$vuetify.theme.themes.light['event-attended'], this.$vuetify.theme.themes.light['event-missed'], this.$vuetify.theme.themes.light['event-rescheduled']],
                chart: {
                    type: 'bar',
                    height: 200,
                    stacked: true,
                    toolbar: {
                        show: false
                    },
                    zoom: {
                        enabled: false
                    }
                },
                responsive: [{
                    breakpoint: 480,
                    options: {
                        legend: {
                            position: 'bottom',
                            offsetX: -10,
                            offsetY: 0
                        }
                    }
                }],
                plotOptions: {
                    bar: {
                        horizontal: false,
                        borderRadius: 10,
                        borderRadiusApplication: 'end', // 'around', 'end'
                        borderRadiusWhenStacked: 'last', // 'all', 'last'
                        dataLabels: {
                            total: {
                                enabled: true,
                                style: {
                                    fontSize: '13px',
                                    fontWeight: 900,
                                    color: '#fff'
                                }
                            }
                        }
                    },
                },
                xaxis: {
                    type: 'category',
                    categories: [],
                },
                legend: {
                    show: false,
                    position: 'right',
                    offsetY: 10
                },
                fill: {
                    opacity: 1
                }
            },
            seriesSessionsChart: [],
            service_tab: 0,
            chart_index: 0,
            loadingSync: false,
            showRescheduleDialog: false,
            showDialogExport: false,
            show_medical_record: false
        }
    },
    computed: {
        ...mapState("schedule", {
            reschedule_requests: state => state.reschedule_requests
        }),
        customerRescheduleRequests() {
            if (!this.customerId) return [];
            
            // Ensure reschedule_requests is an array
            const requests = Array.isArray(this.reschedule_requests) 
                ? this.reschedule_requests 
                : (this.reschedule_requests?.data || []);
            
            return requests.filter(r => 
                r.customers_session.customer_id === this.customerId && r.status === 'pending'
            );
        },
        isStarter() {
            return this.$store.state.auth.user?.access_profile_slug === 'starter';
        },
        filteredSessions() {
            if (!this.customer || !this.customer.sessions) return [];
            
            // Tab 0 is "Geral" - show all sessions
            if (this.service_tab === 0) {
                return this.customer.sessions;
            }
            
            // Get the service_id from the selected contract
            const contractIndex = this.service_tab - 1;
            if (this.customer.active_contracts && this.customer.active_contracts[contractIndex]) {
                const serviceId = this.customer.active_contracts[contractIndex].service_id;
                return this.customer.sessions.filter(session => session.service_id === serviceId);
            }
            
            return [];
        },
        donutSeries() {
            if (!this.filteredSessions || this.filteredSessions.length === 0) {
                return [0, 0, 0];
            }
            
            const attended = this.filteredSessions.filter(s => s.attended).length;
            const missed = this.filteredSessions.filter(s => !s.attended && !s.rescheduled && s.finished).length;
            const rescheduled = this.filteredSessions.filter(s => s.rescheduled).length;
            
            return [attended, missed, rescheduled];
        },
        lastPayment() {
            if (!this.customer || !this.customer.extracts || this.customer.extracts.length === 0) {
                return null;
            }
            // Sort descending by extract_date to always get the most recent payment
            const sorted = [...this.customer.extracts].sort((a, b) => {
                return new Date(b.extract_date) - new Date(a.extract_date);
            });
            return sorted[0];
        },
        nextPaymentDate() {
            if (!this.customer || !this.customer.active_contracts || this.customer.active_contracts.length === 0) {
                return 'Não programado';
            }
            
            // If "Geral" tab, find the nearest payday
            if (this.service_tab === 0) {
                const paydays = this.customer.active_contracts
                    .map(c => c.payday)
                    .filter(p => p)
                    .sort();
                
                return paydays.length > 0 ? dayjs(paydays[0]).format('DD/MM/YYYY') : 'Não programado';
            }
            
            // If specific service tab, get payday from that contract
            const contractIndex = this.service_tab - 1;
            if (this.customer.active_contracts[contractIndex] && this.customer.active_contracts[contractIndex].payday) {
                return dayjs(this.customer.active_contracts[contractIndex].payday).format('DD/MM/YYYY');
            }
            
            return 'Não programado';
        }
    },
    watch: {
        service_tab() {
            this.updateSessionsChart();
        },
        customerId() {
            this.customer = null;
            if (this.customerId) {
                this.getCustomerInfo();
            }
        },
        sessionInfo() {
            this.session_info = this.sessionInfo;
        },
        customer() {
            if (this.customer?.sessions) {
                this.updateSessionsChart();
            }
        }
    },
    mounted() {
        window.$customerInfo = this;
        this.getCustomerInfo();
    },
    beforeDestroy() {
        if (window.$customerInfo === this) {
            window.$customerInfo = null;
        }
    },
    methods: {
        ...mapActions("customers", ["ActionDoGetCustomer", "ActionDoGetCustomers"]),
        ...mapActions("schedule", ["ActionDoGetCalendarEvents", "ActionDoGetRescheduleRequests"]),
        ...mapActions("sessions", ["ActionDoGetSession"]),
        ...mapActions("utils", ["ActionSyncSingleEvent"]),
        getCustomerInfo(update_calendar = false, is_delete = false) {
            if (this.sessionInfo && String(this.sessionInfo.id).includes('tour-fake')) {
                this.customer = {
                    id: 1,
                    name: 'Cliente Tour',
                    customer_doctor: { balance: 15000 },
                    active_contracts: [],
                    sessions: [
                        { attended: true, finished: true, rescheduled: false, session_date: dayjs().format('YYYY-MM-DD'), service: { name: 'Psicoterapia' } }
                    ]
                };
                this.customer_data = this.customer;
                this.session_info = this.sessionInfo;
                return;
            }

            this.ActionDoGetCustomer(this.customerId).then(response => {
                this.customer = response;
                this.customer_data = this.customer;

                this.ActionDoGetSession(this.sessionInfo.id).then(resp_session => {
                    this.session_info = resp_session;
                })

                if (update_calendar) {
                    this.ActionDoGetCalendarEvents();
                    if (is_delete) {
                        this.$emit('closeInfo');
                    }
                }
            }).catch(error => {
                console.error(error);
            });
            
            // Also refresh reschedule requests
            this.ActionDoGetRescheduleRequests().catch(error => {
                console.error('Error loading reschedule requests:', error);
            });
        },
        getAge(birthday) {
            return dayjs().diff(dayjs(birthday), 'year');
        },
        updateSessionsChart() {
            this.seriesSessionsChart = [];
            let unordered = {};

            for (let session of this.filteredSessions) {
                let month_year = dayjs(session.session_date).format('YYYY-MM');
                if (unordered[month_year] == undefined) {
                    unordered[month_year] = {
                        attended: 0,
                        missed: 0,
                        rescheduled_data: 0
                    };
                }

                if (session.attended) {
                    unordered[month_year].attended += 1;
                } else if (session.rescheduled) {
                    unordered[month_year].rescheduled_data += 1;
                } else if (session.finished) {
                    unordered[month_year].missed += 1;
                }
            }

            const ordered = Object.keys(unordered).sort().reduce(
                (obj, key) => { 
                    obj[key] = unordered[key]; 
                    return obj;
                }, 
                {}
            );

            let attended_data = [];
            let missed_data = [];
            let rescheduled_data = [];
            let chart_labels = [];
            for (let month in ordered) {
                attended_data.push(ordered[month].attended);
                missed_data.push(ordered[month].missed);
                rescheduled_data.push(ordered[month].rescheduled_data);
                chart_labels.push(dayjs(month + '01').format('MMM') + ' ' + dayjs(month + '01').format('YY'))
            }

            this.seriesSessionsChart = [
                {
                    name: "Compareceu",
                    data: attended_data
                },
                {
                    name: "Faltou",
                    data: missed_data
                },
                {
                    name: "Remarcou",
                    data: rescheduled_data
                }
            ];

            // Force reactivity by creating a new object
            this.chartOptionsBar = {
                ...this.chartOptionsBar,
                xaxis: {
                    ...this.chartOptionsBar.xaxis,
                    categories: chart_labels
                }
            };
        },
        openConfirmSession() {
            this.session_data = this.session_info;
            this.show_confirm_session = true;
        },
        closeConfirmSession() {
            this.show_confirm_session = false;
            this.session_data = null;
        },
        openAddSession() {
            this.customer_data = this.customer;
            this.show_add_session = true;
        },
        closeAddSession() {
            this.show_add_session = false;
            this.customer_data = null;
        },
        openAddExtract() {
            this.customer_data = this.customer;
            this.show_add_extract = true;
        },
        closeAddExtract() {
            this.show_add_extract = false;
            this.customer_data = null;
        },
        openEditExtract(extract) {
            this.customer_data = this.customer;
            this.extract_data = extract;
            this.show_edit_extract = true;
        },
        closeEditExtract() {
            this.show_edit_extract = false;
            this.extract_data = null;
            this.customer_data = null;
        },
        openDeleteExtract(extract) {
            this.customer_data = this.customer;
            this.extract_data = extract;
            this.show_delete_extract = true;
        },
        closeDeleteExtract() {
            this.show_delete_extract = false;
            this.extract_data = null;
            this.customer_data = null;
        },
        openEditSession(session) {
            this.customer_data = this.customer;
            this.session_data = session;
            this.show_edit_session = true;
        },
        closeEditSession() {
            this.show_edit_session = false;
            this.session_data = null;
            this.customer_data = null;
        },
        openDeleteSession(session) {
            this.customer_data = this.customer;
            this.session_data = session;
            this.show_delete_session = true;
        },
        closeDeleteSession() {
            this.show_delete_session = false;
            this.extract_data = null;
            this.customer_data = null;
        },
        openMedicalRecord(session) {
            this.session_data = session;
            this.show_medical_record = true;
        },
        openExportModal() {
            this.showDialogExport = true;
            this.$nextTick(() => {
                if (this.$refs.exportModal && this.customer) {
                    this.$refs.exportModal.selectedCustomer = this.customer;
                    this.$refs.exportModal.loadSessions();
                }
            });
        },
        formatDate(date, hour = false) {
            if (hour) {
                return dayjs(date).format('DD/MM/YYYY HH:mm');
            } else {
                return dayjs(date).format('DD/MM/YYYY');
            }
        },
        showSessions(customer) {
            this.customer_data = customer;
            this.show_list_sessions = true;
        },
        closeListSessions() {
            this.show_list_sessions = false;
            this.customer_data = null;
        },
        showCustomerPayments(customer) {
            this.customer_data = customer;
            this.show_list_customer_payments = true;
        },
        closeCustomerPayments() {
            this.show_list_customer_payments = false;
            this.customer_data = null;
        },
        getSessionStatusColor(session) {
            if (session.attended) {
                return 'event-attended';
            } else if (session.rescheduled) {
                return 'event-rescheduled';
            } else if (session.finished) {
                return 'event-missed';
            } else {
                return 'event-scheduled';
            }
        },
        getSessionStatusText(session) {
            if (session.attended) {
                return 'Compareceu';
            } else if (session.rescheduled) {
                return 'Remarcou';
            } else if (session.finished) {
                return 'Faltou';
            } else {
                return 'Agendado';
            }
        },
        checkTimeDiscrepancy(session) {
            if (!session || !session.google_calendar_event) {
                return false;
            }

            const googleEvent = session.google_calendar_event.event_data;
            const googleStart = dayjs(googleEvent.start);
            const googleEnd = dayjs(googleEvent.end);
            
            // Session dates are usually YYYY-MM-DD HH:mm:ss strings
            const systemStart = dayjs(session.session_date);
            const systemEnd = dayjs(session.session_date).add(session.session_duration, 'minute');

            // Check if start or end times differ by more than a minute
            const startDiff = Math.abs(googleStart.diff(systemStart, 'minute'));
            const endDiff = Math.abs(googleEnd.diff(systemEnd, 'minute'));

            return startDiff > 1 || endDiff > 1;
        },
        async syncEventToGoogle(session) {
            this.loadingSync = true;
            try {
                await this.ActionSyncSingleEvent({ id: session.id });
                // Refresh session info to get updated google event data
                this.getCustomerInfo(true);
                this.$emit('update');
            } catch (error) {
                console.error("Error syncing event:", error);
            } finally {
                this.loadingSync = false;
            }
        }
    }
}
</script>
